/**
 * Zipchat chat bubble overrides.
 *
 * The bubble (#widget-chat-container) lives inside the app's shadow root
 * (#zipchat-shadow-host) and is positioned with inline styles, so the page CSS
 * cannot reach it and the widget exposes no ::part(). A <style> is injected into
 * the shadow root instead — `!important` there beats the non-important inline
 * declarations.
 *
 * The bubble is lifted above the PDP sticky add-to-cart bar only while the bar
 * is showing. pdp-sticky-atc.js puts `pdp-sticky-visible` on <html>, which the
 * shadow CSS cannot see, so it is mirrored onto the host as an attribute and
 * matched with `:host([data-pdp-sticky-visible])`.
 *
 * The app embed loads late, so the host is waited for; the style is re-added if
 * the widget ever re-renders its shadow content and drops it.
 */

(() => {
  const HOST_SELECTOR = '#zipchat-shadow-host';
  const STYLE_ATTR = 'data-theme-override';
  const STICKY_CLASS = 'pdp-sticky-visible';
  const STICKY_ATTR = 'data-pdp-sticky-visible';
  const CSS = `
    #widget-chat-container {
      z-index: 1000 !important;
      right: 10px !important;
    }
    :host([${STICKY_ATTR}]) #widget-chat-container {
      bottom: 85px !important;
    }
    @media screen and (max-width: 749px) {
      :host([${STICKY_ATTR}]) #widget-chat-container {
        bottom: 65px !important;
      }
    }
  `;

  const ensureStyle = (root) => {
    if (root.querySelector(`style[${STYLE_ATTR}]`)) return;
    const style = document.createElement('style');
    style.setAttribute(STYLE_ATTR, '');
    style.textContent = CSS;
    root.appendChild(style);
  };

  const syncSticky = (host) => {
    host.toggleAttribute(STICKY_ATTR, document.documentElement.classList.contains(STICKY_CLASS));
  };

  const attach = () => {
    const host = document.querySelector(HOST_SELECTOR);
    // `shadowRoot` is null for a closed root — nothing can be done from outside then.
    const root = host?.shadowRoot;
    if (!root) return false;

    ensureStyle(root);
    new MutationObserver(() => ensureStyle(root)).observe(root, { childList: true });

    syncSticky(host);
    new MutationObserver(() => syncSticky(host)).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return true;
  };

  if (attach()) return;

  const observer = new MutationObserver(() => {
    if (attach()) observer.disconnect();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
